import { Card } from '@mui/material';
import { styled } from '@mui/material/styles';

import { px, rem, Theme } from '../../theme';
import { Heading } from '../Heading';

export const StyledCard = styled(Card, {
    shouldForwardProp: property =>
        typeof property === 'string' && !property.startsWith('$'),
})<{ $isMBTCardsOptimizationEnabled: boolean }>`
    ${({ theme, $isMBTCardsOptimizationEnabled }) => `
        padding: ${theme.tggSpacing(rem(3.2), 25.5)};
        margin: ${theme.tggSpacing([rem(3), rem(0.2), 0], [12, 1, 0])};
        margin-inline: ${$isMBTCardsOptimizationEnabled && theme.tggSpacing(0, 0)};
        margin-top: ${$isMBTCardsOptimizationEnabled && theme.tggSpacing(rem(1), 4)};
        border-radius: ${!$isMBTCardsOptimizationEnabled && theme.tggSpacing(rem(1.6), 6.5)};
        box-shadow: ${$isMBTCardsOptimizationEnabled && 'none'};
        ${theme.breakpoints.up('desktop')} {
            padding: ${theme.tggSpacing(rem(4), 16)};
            padding-top: ${$isMBTCardsOptimizationEnabled ? theme.tggSpacing(0, 0) : ''};
        }

        ${theme.breakpoints.up('desktop')} {
            margin: ${theme.tggSpacing([rem(3), 0, 0], [12, 0, 0])};
        }

        ${theme.breakpoints.down(468)} {
            padding: ${$isMBTCardsOptimizationEnabled ? theme.tggSpacing(rem(1), 4) : theme.tggSpacing(rem(2), 8)};
        }

        ${theme.breakpoints.down(405)} {
            padding: ${$isMBTCardsOptimizationEnabled ? theme.tggSpacing(rem(1), 4) : theme.tggSpacing(rem(1.5), 6)};
        }
	`}
`;

export const StyledAsteriskText = styled('div')`
    ${({
        theme,
        $isMBTOptimization,
        $hasPayPal,
    }: {
        theme: Theme;
        $hasPayPal?: boolean;
        $isMBTOptimization?: boolean;
    }) => `
        font-size: 1rem;
        color: ${theme.palette.common.blue};
        margin-top: ${$isMBTOptimization ? theme.tggSpacing(rem(1), 8) : theme.tggSpacing(rem(2), 20)};
        ${theme.breakpoints.up('desktop')} {
            text-align: center;
            margin-top: ${
                $isMBTOptimization
                    ? $hasPayPal
                        ? theme.tggSpacing(rem(1), 4)
                        : theme.tggSpacing(rem(2), 8)
                    : theme.tggSpacing(rem(3), 12)
            };
            margin-bottom: ${$isMBTOptimization && theme.tggSpacing(rem(1), 4)};
            font-size: 1.2rem;
            text-align: center;
        }
	`}
`;

export const StyledSectionHeading = styled(Heading)`
    ${({ theme }: { theme: Theme }) => theme.typography.bodySmallBold};
    ${({ theme }: { theme: Theme }) => `
        margin: ${theme.tggSpacing([rem(3.2), rem(3), 0, rem(3)], [13, 12, 0, 12])};

        ${theme.breakpoints.up('desktop')} {
            text-align: center;
        }
	`}
`;

export const StyledHeader = styled('header')`
    ${({
        theme,
        $isMBTCardsOptimizationEnabled,
    }: {
        theme: Theme;
        $isMBTCardsOptimizationEnabled?: boolean;
    }) => `
        display: flex;
        align-content: center;
        justify-content: space-between;
        margin-bottom: ${theme.tggSpacing(rem(3), 12)};

        ${theme.breakpoints.up('desktop')} {
            margin-bottom: ${
                $isMBTCardsOptimizationEnabled
                    ? theme.tggSpacing(rem(3), 12)
                    : theme.tggSpacing(rem(4), 16)
            };
        }

        ${theme.breakpoints.up('mobile')} {
            justify-content: ${$isMBTCardsOptimizationEnabled && 'center'};
        }
	`}
`;

export const StyledHeading = styled(Heading)<{
    $isMBTCardsOptimizationEnabled?: boolean;
}>`
    ${({
        theme,
        $isMBTCardsOptimizationEnabled,
    }: {
        theme: Theme;
        $isMBTCardsOptimizationEnabled?: boolean;
    }) => `
        font-size: ${$isMBTCardsOptimizationEnabled && theme.tggSpacing(rem(1.9), 7.5)};

        ${theme.breakpoints.up('mobile')} {
            font-size: ${$isMBTCardsOptimizationEnabled && theme.tggSpacing(rem(2.1), 8.5)};
        }
	`}
`;

export const StyledFixedProductOptions = styled('div')`
    ${({
        theme,
        $isMBTCardsOptimizationEnabled,
    }: {
        theme: Theme;
        $isMBTCardsOptimizationEnabled?: boolean;
    }) => `
            display: flex;
        gap: ${$isMBTCardsOptimizationEnabled ? theme.tggSpacing(rem(0.5), 2) : theme.tggSpacing(rem(1.3), 5)};

        ${theme.breakpoints.up(361)} {
            gap: ${$isMBTCardsOptimizationEnabled ? theme.tggSpacing(rem(1), 4) : theme.tggSpacing(rem(1.3), 5)};
        }

        justify-content: center;

        & > article {
            flex: 1;
        }

        ${theme.breakpoints.up('desktop')} {
            gap: ${$isMBTCardsOptimizationEnabled ? theme.tggSpacing(rem(1), 4) : theme.tggSpacing(rem(1.3), 5)};
        }
	`}
`;

export const WhiteWrapper = styled('div')`
    z-index: 1;
    background: #fff;
`;

export const StyledDivider = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        position: relative;
        margin: ${theme.tggSpacing([rem(3.5), 0], [14, 0])};

        div {
            position: absolute;
            top: ${theme.tggSpacing(rem(-0.9), -3.5)};
            left: calc(50% -  ${theme.tggSpacing(rem(2.5), 6)});
            width: ${theme.tggSpacing(rem(5), 20)};
            font-family: thegymgroupsans-Headline, sans-serif;
            text-align: center;
            background: #fff;
        }

        ${theme.breakpoints.up('desktop')} {
            margin: ${theme.tggSpacing([rem(4), 0], [16, 0])};
        }
	`}
`;

export const StyledButtonContainer = styled('div')<{
    isrejoin?: boolean;
    $isMBTOptimization?: boolean;
    $hasPayPal?: boolean;
    $showFreeFreezeNotice?: boolean;
}>`
    ${({
        theme,
        isrejoin,
        $isMBTCardsOptimizationEnabled,
        $isMBTOptimization,
        $showFreeFreezeNotice,
        $hasPayPal,
    }: {
        theme: Theme;
        isrejoin?: boolean;
        $isMBTCardsOptimizationEnabled?: boolean;
        $isMBTOptimization?: boolean;
        $showFreeFreezeNotice?: boolean;
        $hasPayPal?: boolean;
    }) => `
        display: flex;
        flex-direction: ${isrejoin ? 'column' : 'row'};
        gap: ${theme.tggSpacing(rem(1), 4)};
        justify-content: center;
        max-width: ${isrejoin && '50%'};
        padding: ${isrejoin && theme.tggSpacing([0, px(30)], [0, 7.5])};
        margin: ${
            isrejoin
                ? theme.tggSpacing([px(40), 'auto'], [10, 'auto'])
                : $isMBTOptimization && ($hasPayPal || $showFreeFreezeNotice)
                  ? theme.tggSpacing([px(5), 0, 0, 0], [1.25, 0, 0, 0])
                  : theme.tggSpacing([px(20), 0, 0, 0], [5, 0, 0, 0])
        };

        ${theme.breakpoints.down('desktop')} {
            max-width: ${isrejoin && '100%'};
            margin-top: ${
                $isMBTOptimization && $hasPayPal
                    ? theme.tggSpacing(rem(1), 4)
                    : theme.tggSpacing(px(20), 5)
            };
        }

        ${
            $isMBTCardsOptimizationEnabled
                ? `
                    flex-direction: column;
                    max-width: ${theme.tggSpacing(rem(38), 152)};
                    margin-inline: ${theme.tggSpacing('auto', 'auto')};
                `
                : ''
        };

        button {
            margin-top: ${theme.tggSpacing(rem(1), 4)};
        }
    `}
`;
